import { deriveQueryStringHelpersFromSearchString } from '@shared/core/queryString';
import { parseDateRangeQueryParams, resolveStayDateQueryParams } from '@shared/utils/dateRangeQueryParams';
import { HotelSearchContextParams, PRIORITIZE_TAG_PARAM, TRANSFERRED_STAYS_QUERY_PARAMS } from '@shared/constants/accommodations';

const MAX_DESTINATION_LENGTH = 80;

/** Search params kept in og:url. Tracking params are dropped. Filter and discount keys are listed here because they live in stays, which guest code must not import. */
const CANONICAL_PARAMS = new Set(
  [
    ...Object.values(HotelSearchContextParams),
    ...Object.values(TRANSFERRED_STAYS_QUERY_PARAMS),
    PRIORITIZE_TAG_PARAM,
    'destination',
    'lat',
    'lng',
    'googlePlaceId',
    'radius',
    'rooms',
    'sort',
    'honeymoonSearch',
    'currency',
    'cur',
    'facilities',
    'starRating',
    'userRating',
    'priceMin',
    'priceMax',
    'cug',
    'pickedByCouple',
    'showAvailableOnly',
    'propertyCategories',
    'brands',
    'freeCancellation',
    'cashFundCovered',
    'showEligibleOnly',
    'joyHotelDiscountCode',
    'joyHotelDiscountRule',
  ].map(key => key.toLowerCase()),
);

// Caps by code point (not UTF-16 unit) so the cut never splits a surrogate pair.
const toDisplayDestination = (destination: string | undefined): string | undefined => {
  const trimmed = destination?.trim();
  if (!trimmed) return undefined;
  const codePoints = Array.from(trimmed);
  return codePoints.length > MAX_DESTINATION_LENGTH ? `${codePoints.slice(0, MAX_DESTINATION_LENGTH).join('').trimEnd()}…` : trimmed;
};

/** Builds the search preview from the URL alone, with no event or hotel lookup. */
export const buildHotelSearchOgContent = (pathname: string, search: string) => {
  const helper = deriveQueryStringHelpersFromSearchString(search);
  const { checkIn, checkOut } = resolveStayDateQueryParams(helper);
  const dateRange = parseDateRangeQueryParams(checkIn, checkOut);

  const params = new URLSearchParams();
  new URLSearchParams(search).forEach((value, key) => {
    if (CANONICAL_PARAMS.has(key.toLowerCase())) params.append(key, value);
  });
  if (dateRange) {
    params.append('checkin', dateRange.checkIn);
    params.append('checkout', dateRange.checkOut);
  }
  const query = params.toString();

  return {
    dateRange,
    destination: toDisplayDestination(helper.getValueString('destination')),
    // Trailing slashes are stripped so `/hotel/search/` and `/hotel/search` share one identity.
    canonicalUrl: `https://withjoy.com${pathname.replace(/\/+$/, '')}${query ? `?${query}` : ''}`,
  };
};
